import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { FaEye, FaSearch, FaFilter, FaCalendarAlt, FaUser, FaStore, FaMoneyBillWave, FaCheckCircle, FaClock, FaTimesCircle, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getStoreBranches } from '../../../redux/Slices/storeBranchesSlice';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
} from '@tanstack/react-table';

const BranchOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 10,
        current_page: 1,
        pages_count: 1
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { branches } = useSelector((state) => state.storeBranches);

    const BASE_URL = process.env.REACT_APP_BASE_URL;

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch (error) {
            }
        }
        return null;
    };

    const getUserRole = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.role;
            } catch (error) {
            }
        }
        return null;
    };

    // دالة للحصول على اسم الفرع بناءً على branch_id أو branch_name
    const getBranchName = (branchId, branchName) => {
        // إذا كان لدينا branch_name مباشرة (لـ pharmacy role)
        if (branchName && branchName !== 'غير محدد') {
            return branchName;
        }
        
        // إذا كان لدينا branch_id (لـ store role)
        if (branchId && branches && branches.length > 0) {
            const branch = branches.find(b => b.id === branchId);
            return branch ? (branch.market_name || branch.name || 'غير محدد') : 'غير محدد';
        }
        
        return 'غير محدد';
    };

    const fetchOrders = async (page = 1) => {
        try {
            setLoading(true);
            const token = getAuthToken();
            const userRole = getUserRole();
            
            if (!token) {
                toast.error('لا يوجد token للمصادقة', { containerId: 'main-toast-container' });
                navigate('/login');
                return;
            }

            // تحديد الـ endpoint بناءً على الـ role
            let endpoint = '';
            if (userRole === 'store') {
                endpoint = `${BASE_URL}/store_orders?page=${page}`;
            } else if (userRole === 'pharmacy') {
                endpoint = `${BASE_URL}/main-branch/orders?page=${page}`;
            } else {
                toast.error('غير مصرح لك بالوصول لهذه الصفحة');
                navigate('/');
                return;
            }

            const response = await axios.get(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data) {
                let ordersData = [];
                let paginationData = {
                    total: 0,
                    per_page: 10,
                    current_page: 1,
                    pages_count: 1
                };

                // للـ store role
                if (response.data.result && response.data.result.data) {
                    ordersData = response.data.result.data;
                    paginationData = response.data.result.pagination || paginationData;
                }
                // للـ pharmacy role
                else if (response.data.data) {
                    ordersData = response.data.data;
                    // تحويل البيانات لتطابق هيكل store
                    ordersData = ordersData.map(order => ({
                        id: order.id,
                        user_name: order.client,
                        client_phone: order.client_phone || 'غير محدد',
                        prof_img: '',
                        items_count: 1, // افتراضي
                        status: order.status,
                        status_value: order.status,
                        subtotal: order.total,
                        discount_total: 0,
                        delivery_fees: 0,
                        extra_fees: 0,
                        total: order.total,
                        payment_method: 'cod', // افتراضي
                        payment_status: 'pending', // افتراضي
                        placed_at: order.created_at,
                        store_id: 0,
                        branch_id: null, // لا يوجد branch_id في البيانات
                        branch_name: order.branch || 'غير محدد',
                        nearest_branch: order.nearest_branch || null
                    }));
                    // محاولة الحصول على pagination من response
                    if (response.data.pagination) {
                        paginationData = response.data.pagination;
                    } else if (response.data.result && response.data.result.pagination) {
                        paginationData = response.data.result.pagination;
                    } else {
                    paginationData = {
                        total: ordersData.length,
                            per_page: 10,
                            current_page: currentPage,
                            pages_count: Math.ceil(ordersData.length / 10)
                        };
                    }
                } else {
                    throw new Error('فشل في جلب الأوردرات');
                }

                setOrders(ordersData);
                setPagination(paginationData);
                setCurrentPage(page);
            } else {
                throw new Error('فشل في جلب الأوردرات');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'فشل في جلب الأوردرات', { containerId: 'main-toast-container' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // جلب بيانات الفروع أولاً
        dispatch(getStoreBranches());
        fetchOrders(currentPage);
    }, [dispatch, currentPage]);

    // دالة تغيير حالة الطلب
    const handleChangeStatus = async (orderId, newStatus) => {
        try {
            const token = getAuthToken();
            const userRole = getUserRole();
            
            if (!token) {
                toast.error('لا يوجد token للمصادقة', { containerId: 'main-toast-container' });
                return;
            }

            // التحقق من أن المستخدم pharmacy
            if (userRole !== 'pharmacy') {
                toast.error('غير مصرح لك بتغيير حالة الطلب', { containerId: 'main-toast-container' });
                return;
            }

            const endpoint = `${BASE_URL}/main-branch/orders/${orderId}/change-status`;
            
            const response = await axios.post(endpoint, 
                { status: newStatus },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                    },
                }
            );

            if (response.data) {
                toast.success(`تم تغيير حالة الطلب إلى ${newStatus === 'preparing' ? 'قيد التحضير' : 'ملغي'} بنجاح`, { containerId: 'main-toast-container' });
                // إعادة تحميل الطلبات
                fetchOrders(currentPage);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'فشل في تغيير حالة الطلب', { containerId: 'main-toast-container' });
        }
    };

    const getStatusBadge = (status) => {
        // للـ pharmacy role (نص)
        if (typeof status === 'string') {
            switch (status) {
                case 'placed':
                    return <span className="badge bg-info">تم الطلب</span>;
                case 'confirmed':
                    return <span className="badge bg-info">مؤكد</span>;
                case 'preparing':
                    return <span className="badge bg-warning">قيد التحضير</span>;
                case 'ready':
                    return <span className="badge bg-primary">جاهز</span>;
                case 'completed':
                    return <span className="badge bg-success">مكتمل</span>;
                case 'delivered':
                    return <span className="badge bg-success">تم التوصيل</span>;
                case 'cancelled':
                    return <span className="badge bg-danger">ملغي</span>;
                default:
                    return <span className="badge bg-secondary">{status}</span>;
            }
        }
        
        // للـ store role (أرقام)
        switch (status) {
            case 1:
                return <span className="badge bg-info">مؤكد</span>;
            case 2:
                return <span className="badge bg-warning">قيد التحضير</span>;
            case 3:
                return <span className="badge bg-primary">جاهز</span>;
            case 4:
                return <span className="badge bg-success">تم التوصيل</span>;
            case 5:
                return <span className="badge bg-danger">ملغي</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const getStatusColor = (status) => {
        // للـ pharmacy role (نص)
        if (typeof status === 'string') {
            switch (status) {
                case 'placed':
                    return '#ffc107';
                case 'confirmed':
                    return '#17a2b8';
                case 'preparing':
                    return '#007bff';
                case 'ready':
                    return '#28a745';
                case 'completed':
                    return '#28a745';
                case 'delivered':
                    return '#28a745';
                case 'cancelled':
                    return '#dc3545';
                default:
                    return '#6c757d';
            }
        }
        
        // للـ store role (أرقام)
        switch (status) {
            case 1:
                return '#ffc107';
            case 2:
                return '#17a2b8';
            case 3:
                return '#007bff';
            case 4:
                return '#28a745';
            case 5:
                return '#dc3545';
            default:
                return '#6c757d';
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            calendar: 'gregory'
        });
    };

    // Add phone styling
    const phoneStyles = `
        .phone-display {
            background: #eff6ff;
            color: #1e40af;
            padding: 0.5rem 1rem;
            border-radius: 8px;
            font-size: 0.875rem;
            border: 1px solid #bfdbfe;
            font-weight: 500;
            direction: ltr;
        }
    `;

    // Column helper for table configuration
    const columnHelper = createColumnHelper();

    // Table columns configuration
    const columns = useMemo(
        () => [
            columnHelper.accessor('id', {
                id: 'id',
                header: '#',
                cell: ({ row }) => (
                    <span className="fw-semibold text-muted">#{row.index + 1}</span>
                ),
                size: 60,
            }),
            columnHelper.accessor('user_name', {
                id: 'user_name',
                header: 'العميل',
                cell: ({ getValue }) => (
                    <div className="text-center">
                        <h6 className="mb-0 fw-semibold" style={{ fontSize: '12px' }}>
                            {getValue() || 'غير محدد'}
                        </h6>
                    </div>
                ),
                size: 150,
            }),
            columnHelper.accessor('client_phone', {
                id: 'client_phone',
                header: 'رقم التليفون',
                cell: ({ getValue }) => (
                    <div className="text-center">
                        <div className="phone-display">
                            {getValue()}
                        </div>
                    </div>
                ),
                size: 140,
            }),
            columnHelper.accessor('prof_img', {
                id: 'prof_img',
                header: 'صورة العميل',
                cell: ({ getValue }) => (
                    <div className="d-flex justify-content-center align-items-center">
                        {getValue() && getValue() !== "" ? (
                            <img 
                                src={getValue()} 
                                alt="صورة العميل"
                                style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '50%' }}
                            />
                        ) : (
                            <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                <FaUser className="text-white" style={{ fontSize: '16px' }} />
                            </div>
                        )}
                    </div>
                ),
                size: 100,
            }),
            columnHelper.accessor('branch_name', {
                id: 'branch_name',
                header: 'الفرع',
                cell: ({ row }) => (
                    <div className="text-center">
                        <span className="badge bg-info text-white" style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {getBranchName(row.original.branch_id, row.original.branch_name)}
                        </span>
                    </div>
                ),
                size: 200,
            }),
            // إضافة عمود أقرب فرع للعميل إذا كان متاحاً
            columnHelper.accessor('nearest_branch', {
                id: 'nearest_branch',
                header: 'أقرب فرع',
                cell: ({ row }) => {
                    const nearest = row.original.nearest_branch;
                    return nearest ? (
                        <div className="text-center">
                            <span className="badge bg-secondary text-white" style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                {nearest.name} <br />
                                <small>({nearest.distance_km} كم)</small>
                            </span>
                        </div>
                    ) : (
                        <span className="badge bg-light text-dark">غير محدد</span>
                    );
                },
                size: 180,
            }),
            columnHelper.accessor('items_count', {
                id: 'items_count',
                header: 'عدد المنتجات',
                cell: ({ getValue }) => (
                    <div className="text-center">
                        <span className="badge bg-primary text-white">
                            {getValue()} منتج
                        </span>
                    </div>
                ),
                size: 120,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'الحالة',
                cell: ({ getValue }) => (
                    <div className="text-center">
                        {getStatusBadge(getValue())}
                    </div>
                ),
                filterFn: (row, id, value) => {
                    if (value === 'all') return true;
                    const status = row.original.status;
                    return status === value;
                },
                size: 150,
            }),
            columnHelper.accessor('total', {
                id: 'total',
                header: 'المبلغ الإجمالي',
                cell: ({ getValue }) => (
                    <div className="text-center">
                        <span className="fw-bold" style={{ color: '#28a745' }}>
                            <FaMoneyBillWave className="mx-1" />
                            {getValue()} ج.م
                        </span>
                    </div>
                ),
                size: 150,
            }),
            columnHelper.accessor('payment_method', {
                id: 'payment_method',
                header: 'طريقة الدفع',
                cell: ({ getValue }) => (
                    <div className="text-center">
                        <span className={`badge ${getValue() === 'cod' ? 'bg-info' : 'bg-secondary'} text-white`}>
                            {getValue() === 'cod' ? 'الدفع عند الاستلام' : getValue()}
                        </span>
                    </div>
                ),
                size: 150,
            }),
            columnHelper.accessor('payment_status', {
                id: 'payment_status',
                header: 'حالة الدفع',
                cell: ({ getValue }) => (
                    <div className="text-center">
                        <span className={`badge ${getValue() === 'pending' ? 'bg-warning' : getValue() === 'paid' ? 'bg-success' : 'bg-danger'} text-white`}>
                            {getValue() === 'pending' ? 'في الانتظار' : 
                             getValue() === 'paid' ? 'مدفوع' : 
                             getValue() === 'failed' ? 'فشل' : getValue()}
                        </span>
                    </div>
                ),
                size: 120,
            }),
            columnHelper.accessor('placed_at', {
                id: 'placed_at',
                header: 'تاريخ الطلب',
                cell: ({ getValue }) => (
                    <div className="text-center">
                        <div className="d-flex align-items-center justify-content-center">
                            <FaCalendarAlt className="mx-1 text-muted" />
                            <small>{formatDate(getValue())}</small>
                        </div>
                    </div>
                ),
                size: 180,
            }),
            columnHelper.display({
                id: 'actions',
                header: 'الإجراءات',
                cell: ({ row }) => {
                    const order = row.original;
                    const userRole = getUserRole();
                    return (
                        <div className="text-center">
                            <div className="d-flex gap-1 justify-content-center align-items-center" style={{ whiteSpace: 'nowrap' }}>
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => {
                                        if (userRole === 'store') {
                                            navigate(`/store/orders/${order.id}`);
                                        } else if (userRole === 'pharmacy') {
                                            navigate(`/main-branch/orders/${order.id}`);
                                        }
                                    }}
                                    title="عرض التفاصيل"
                                >
                                    <FaEye className="mx-1" />
                                    عرض
                                </button>
                                
                                {/* أزرار تغيير الحالة للـ pharmacy role فقط */}
                                {userRole === 'pharmacy' && order.status !== 'cancelled' && order.status !== 'preparing' && (
                                    <>
                                        <button
                                            className="btn btn-sm btn-outline-success"
                                            onClick={() => handleChangeStatus(order.id, 'preparing')}
                                            title="قيد التحضير"
                                            disabled={order.status === 'preparing'}
                                        >
                                            <FaClock className="mx-1" />
                                            تحضير
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => {
                                                if (window.confirm('هل أنت متأكد من إلغاء هذا الطلب؟')) {
                                                    handleChangeStatus(order.id, 'cancelled');
                                                }
                                            }}
                                            title="إلغاء الطلب"
                                        >
                                            <FaTimesCircle className="mx-1" />
                                            إلغاء
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                },
                size: 280,
            }),
        ],
        [navigate, getBranchName, getStatusBadge, formatDate, getUserRole]
    );

    // Table configuration
    const table = useReactTable({
        data: orders || [],
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    const filteredOrders = orders.filter(order => {
        const matchesSearch = (order.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            order.id.toString().includes(searchTerm) ||
                            (order.status_value || '').toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages_count) {
            fetchOrders(page);
        }
    };

    const renderPagination = () => {
        if (!pagination) return null;

        return (
            <div className="card-footer w-100 bg-light py-2 px-2">
                <div className="row align-items-center g-0">
                    <div className="col-md-6 px-2">
                        <div className="d-flex align-items-center gap-1 flex-wrap">
                            <span className="text-muted">الانتقال إلى الصفحة</span>
                            <div className="input-group" style={{ width: 'auto' }}>
                                <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                    #
                                </span>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={currentPage}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (!isNaN(value) && value > 0) {
                                            const totalPages = pagination?.pages_count || 1;
                                            if (value <= totalPages) {
                                                handlePageChange(value);
                                            }
                                        }
                                    }}
                                    min="1"
                                    max={pagination?.pages_count || 1}
                                    style={{ width: '60px' }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 px-2">
                        <div className="d-flex justify-content-end gap-1 flex-wrap">
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage <= 1}
                            >
                                الأولى
                            </button>
                            <button 
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage <= 1}
                            >
                                السابقة
                            </button>
                            <span className="d-flex align-items-center px-3">
                                صفحة {pagination.current_page || currentPage} من {pagination.pages_count || 1}
                            </span>
                            <button 
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= (pagination.pages_count || 1)}
                            >
                                التالية
                            </button>
                            <button 
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => handlePageChange(pagination.pages_count || 1)}
                                disabled={currentPage >= (pagination.pages_count || 1)}
                            >
                                الأخيرة
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">جاري التحميل...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <h4 className="mb-0 fw-bold" style={{ color: '#184194' }}>
                                        <FaStore className="mx-2" />
                                        الأوردرات
                                    </h4>
                                    <p className="text-muted mb-0 mt-2">إدارة جميع الأوردرات</p>
                                </div>
                               
                            </div>
                        </div>

                        <div className="card-body">
                            {/* Search and Filter Controls */}
                            <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="card shadow-sm card-modern mb-4"
                            >
                                <div className="card-body p-3">
                                    <div className="row align-items-center">
                                        <div className="col-md-6">
                                            <div className="search-container position-relative">
                                                <div className="input-group search-input-group">
                                                    <span className="input-group-text search-icon">
                                                        <FaSearch className="text-white" />
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className="form-control search-input"
                                                        placeholder="البحث في الطلبات..."
                                                        value={globalFilter ?? ''}
                                                        onChange={(e) => setGlobalFilter(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 text-end">
                                            <div className="d-flex gap-2 justify-content-end align-items-center">
                                                {(globalFilter || columnFilters.length > 0) && (
                                                    <span className="badge bg-info text-white mx-2">
                                                        {table.getFilteredRowModel().rows.length} من {orders?.length || 0} نتيجة
                                                    </span>
                                                )}
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => setShowFilters(!showFilters)}
                                                    style={{ borderRadius: '8px' }}
                                                >
                                                    <FaFilter className="mx-1" />
                                                    {showFilters ? 'إخفاء المرشحات' : 'إظهار المرشحات'}
                                                </button>
                                                    <button
                                                    className="btn btn-outline-primary"
                                                        onClick={() => {
                                                        setGlobalFilter('');
                                                        setColumnFilters([]);
                                                        setSorting([]);
                                                    }}
                                                    style={{ borderRadius: '8px' }}
                                                >
                                                    مسح الكل
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Advanced Filters */}
                                    {showFilters && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mt-3 pt-3 border-top"
                                        >
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold">فلترة حسب الحالة:</label>
                                                    <select
                                                        className="form-select"
                                                        value={columnFilters.find(f => f.id === 'status')?.value || 'all'}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setColumnFilters(prev => 
                                                                prev.filter(f => f.id !== 'status').concat(
                                                                    value === 'all' ? [] : [{ id: 'status', value }]
                                                                )
                                                            );
                                                        }}
                                                    >
                                                        <option value="all">جميع الحالات</option>
                                                        <option value="placed">تم وضع الطلب</option>
                                                        <option value="confirmed">تم تأكيد الطلب</option>
                                                        <option value="preparing">قيد التحضير</option>
                                                        <option value="ready">جاهز للتسليم</option>
                                                        <option value="completed">مكتمل</option>
                                                        <option value="delivered">تم التوصيل</option>
                                                        <option value="cancelled">ملغي</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label fw-semibold">فلترة حسب حالة الدفع:</label>
                                                    <select
                                                        className="form-select"
                                                        value={columnFilters.find(f => f.id === 'payment_status')?.value || 'all'}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setColumnFilters(prev => 
                                                                prev.filter(f => f.id !== 'payment_status').concat(
                                                                    value === 'all' ? [] : [{ id: 'payment_status', value }]
                                                                )
                                                            );
                                                        }}
                                                    >
                                                        <option value="all">جميع حالات الدفع</option>
                                                        <option value="pending">في الانتظار</option>
                                                        <option value="paid">مدفوع</option>
                                                        <option value="failed">فشل</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                            </motion.div>

                            {/* Modern Data Table */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="card shadow-sm card-modern"
                            >
                                <div className="card-body p-0">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">جاري التحميل...</span>
                                            </div>
                                        </div>
                                    ) : !Array.isArray(orders) || orders.length === 0 ? (
                                        <div className="text-center py-5">
                                            <div className="text-muted">
                                                <FaStore size={48} className="mb-3" />
                                                <p>لا توجد طلبات في الوقت الحالي</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="table-responsive" style={{ 
                                            overflowX: 'auto', 
                                            overflowY: 'visible',
                                            maxHeight: 'none'
                                        }}>
                                            <table className="table mb-0 table-modern" style={{
                                                backgroundColor: 'transparent',
                                                '--bs-table-striped-bg': 'transparent',
                                                '--bs-table-hover-bg': 'rgba(0,0,0,0.05)',
                                                minWidth: '1400px',
                                                width: '100%'
                                            }}>
                                                <thead className="table-light">
                                                    {table.getHeaderGroups().map(headerGroup => (
                                                        <tr key={headerGroup.id}>
                                                            {headerGroup.headers.map(header => (
                                                                <th
                                                                    key={header.id}
                                                                    className="border-0 fw-semibold text-center"
                                                                    style={{
                                                                        backgroundColor: 'transparent',
                                                                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                                        userSelect: 'none',
                                                                        width: header.getSize()
                                                                    }}
                                                                    onClick={header.column.getToggleSortingHandler()}
                                                                >
                                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                                        {header.column.getCanSort() && (
                                                                            <span>
                                                                                {header.column.getIsSorted() === 'asc' ? (
                                                                                    <FaSortUp className="text-primary" />
                                                                                ) : header.column.getIsSorted() === 'desc' ? (
                                                                                    <FaSortDown className="text-primary" />
                                                                                ) : (
                                                                                    <FaSort className="text-muted" />
                                                                                )}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </thead>
                                                <tbody>
                                                    {table.getRowModel().rows.map((row, index) => (
                                                        <motion.tr
                                                            key={row.id}
                                                            initial={{ opacity: 0, x: -20 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ duration: 0.3, delay: index * 0.05 }}
                                                            style={{ backgroundColor: 'transparent' }}
                                                            className="table-row-hover"
                                                        >
                                                            {row.getVisibleCells().map(cell => (
                                                                <td
                                                                    key={cell.id}
                                                                    className="border-0 text-center align-middle"
                                                                    style={{
                                                                        backgroundColor: 'transparent',
                                                                        width: cell.column.getSize()
                                                                    }}
                                                                >
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                                            ))}
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                                    </div>
                                )}
                            </div>

                                {/* Table Pagination - Hidden */}
                                {false && table.getPageCount() > 1 && (
                                    <div className="card-footer w-100 bg-light">
                                        <div className="row align-items-center">
                                            <div className= "col-md-6">
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <span className="text-muted">الانتقال إلى الصفحة</span>
                                                    <div className="input-group" style={{ width: 'auto' }}>
                                                        <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                                            #
                                                        </span>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm"
                                                            value={table.getState().pagination.pageIndex + 1}
                                                            onChange={(e) => {
                                                                const value = parseInt(e.target.value);
                                                                if (!isNaN(value) && value > 0 && value <= table.getPageCount()) {
                                                                    table.setPageIndex(value - 1);
                                                                }
                                                            }}
                                                            min="1"
                                                            max={table.getPageCount()}
                                                            style={{ width: '60px' }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="d-flex justify-content-end gap-2 flex-wrap">
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => table.setPageIndex(0)}
                                                        disabled={!table.getCanPreviousPage()}
                                                    >
                                                        الأولى
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => table.previousPage()}
                                                        disabled={!table.getCanPreviousPage()}
                                                    >
                                                        السابقة
                                                    </button>
                                                    <span className="d-flex align-items-center px-3">
                                                        صفحة {table.getState().pagination.pageIndex + 1} من {table.getPageCount()}
                                                    </span>
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => table.nextPage()}
                                                        disabled={!table.getCanNextPage()}
                                                    >
                                                        التالية
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                                        disabled={!table.getCanNextPage()}
                                                    >
                                                        الأخيرة
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                </div>
                            )}
                            </motion.div>

                            {/* API Pagination */}
                            {pagination && pagination.pages_count > 1 && (
                                <div className="card-footer w-100 bg-light py-2 px-2">
                                    <div className="row align-items-center g-0">
                                        <div className="col-md-6 px-2">
                                            <div className="d-flex align-items-center gap-1 flex-wrap">
                                                <span className="text-muted">الانتقال إلى الصفحة</span>
                                                <div className="input-group" style={{ width: 'auto' }}>
                                                    <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                                        #
                                                    </span>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        value={currentPage}
                                                        onChange={(e) => {
                                                            const value = parseInt(e.target.value);
                                                            if (!isNaN(value) && value > 0) {
                                                                const totalPages = pagination?.pages_count || 1;
                                                                if (value <= totalPages) {
                                                                    handlePageChange(value);
                                                                }
                                                            }
                                                        }}
                                                        min="1"
                                                        max={pagination?.pages_count || 1}
                                                        style={{ width: '60px' }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6 px-2">
                                            <div className="d-flex justify-content-end gap-1 flex-wrap">
                                                <button
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={currentPage <= 1}
                                                >
                                                    الأولى
                                                </button>
                                                <button 
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                    disabled={currentPage <= 1}
                                                >
                                                    السابقة
                                                </button>
                                                <span className="d-flex align-items-center px-3">
                                                    صفحة {pagination.current_page || currentPage} من {pagination.pages_count || 1}
                                                </span>
                                                <button 
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                    disabled={currentPage >= (pagination.pages_count || 1)}
                                                >
                                                    التالية
                                                </button>
                                                <button 
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => handlePageChange(pagination.pages_count || 1)}
                                                    disabled={currentPage >= (pagination.pages_count || 1)}
                                                >
                                                    الأخيرة
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BranchOrders;

// CSS للجدول المحسن
const styles = `
.table-modern {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    min-width: 1400px;
}

.table-modern thead th {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    color: #495057;
    padding: 1rem 0.75rem;
    vertical-align: middle;
}

.table-modern tbody tr {
    border-bottom: 1px solid #f1f3f4;
    transition: all 0.2s ease;
}

.table-modern tbody tr:last-child {
    border-bottom: none;
}

.table-modern td {
    padding: 1rem 0.75rem;
    vertical-align: middle;
    white-space: normal;
    word-wrap: break-word;
    max-width: 200px;
    min-width: 120px;
}

.card-modern {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.card-modern:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.card-modern .card-body {
    padding: 1.5rem;
}

.table-responsive {
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
    max-height: none;
}

/* Custom Horizontal Scrollbar for Table */
.table-responsive::-webkit-scrollbar {
    height: 8px;
    width: 0;
}

.table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border-radius: 4px;
    transition: all 0.3s ease;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%);
    transform: scale(1.1);
}

/* Firefox Horizontal Scrollbar */
.table-responsive {
    scrollbar-width: thin;
    scrollbar-color: #184194 #f1f1f1;
}

/* Search Input Styling */
.search-container {
    position: relative;
}

.search-input-group {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.search-input-group:focus-within {
    box-shadow: 0 4px 8px rgba(24, 65, 148, 0.2);
    transform: translateY(-1px);
}

.search-icon {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border: none;
    color: white;
    padding: 0.75rem 1rem;
}

.search-input {
    border: none;
    padding: 0.75rem 1rem;
    font-size: 14px;
    background: #fff;
}

.search-input:focus {
    box-shadow: none;
    border: none;
    outline: none;
}

.btn-outline-secondary {
    border-color: #6c757d;
    color: #6c757d;
    transition: all 0.2s ease;
}

.btn-outline-secondary:hover {
    background-color: #6c757d;
    border-color: #6c757d;
    color: white;
    transform: translateY(-1px);
}

.btn-outline-primary {
    border-color: #184194;
    color: #184194;
    transition: all 0.2s ease;
}

.btn-outline-primary:hover {
    background-color: #184194;
    border-color: #184194;
}

.table-row-hover:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
}

/* Phone display styling */
.phone-display {
    background: #eff6ff;
    color: #1e40af;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #bfdbfe;
    font-weight: 500;
    direction: ltr;
}

@media (max-width: 768px) {
    .table-modern {
        min-width: 1200px;
    }
    
    .table-modern th,
    .table-modern td {
        padding: 0.5rem 0.25rem;
        font-size: 0.875rem;
        max-width: 150px;
        min-width: 80px;
    }
    
    .search-input-group {
        margin-bottom: 1rem;
    }
    
    .search-input {
        font-size: 16px; /* منع التكبير في iOS */
    }
}
`;

// إضافة الـ CSS للصفحة
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}
